import { useState, Suspense, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { useFetchClubs } from "../hooks/useFetchClubs";
import Layout from "../components/Layout";
import CityDropDown from "../components/CityDropDown";
import FilterComp from "../components/FilterComp";
import useUserLocation from "../hooks/useUserLocation";
import Hero from "../components/Hero";
import CardLoader from "../components/CardLoader";
import LoadingSpinner from "../components/LoadingSpinner";
import Card from "../components/Card";
import { getDistanceKm, getItemCoordinates, sortByLocation } from "../utils/locationSort";
import AnimatedSearchBar from "../components/AnimatedSearchBar";
import NoContentAvailable from "../components/NoContentAvailable";
import PageSeoContent from "../components/PageSeoContent";
import LocationRetryPrompt from "../components/LocationRetryPrompt";
import useRequestLocationAccess from "../hooks/useRequestLocationAccess";
import {
  currentCity,
  setManualSelection,
  setSelected,
  userCurrentCity,
} from "../redux/reducers/citySelectorSlice";
import {
  getClubsCityEntry,
  getClubsCitySlug,
  resolveClubsCitySeo,
  resolveRouteSeo,
  getCanonicalUrl,
} from "../config/seo";
import {
  getCityLabelFromSlug,
  getClubsListPath,
  resolveSupportedCityLabel,
  resolveSupportedCitySlug,
  DEFAULT_CITY_SLUG,
} from "../utils/clubPath";

const cityCoordinates = {
  Mumbai: { latitude: 19.076, longitude: 72.8777 },
  Thane: { latitude: 19.197, longitude: 72.9635 },
  Delhi: { latitude: 28.7041, longitude: 77.1025 },
  "Delhi NCR": { latitude: 28.7041, longitude: 77.1025 },
  "New Delhi": { latitude: 28.6139, longitude: 77.209 },
  Bangalore: { latitude: 12.9716, longitude: 77.5946 },
  Bengaluru: { latitude: 12.9716, longitude: 77.5946 },
  Hyderabad: { latitude: 17.385, longitude: 78.4867 },
  Chennai: { latitude: 13.0827, longitude: 80.2707 },
  Kolkata: { latitude: 22.5726, longitude: 88.3639 },
  Pune: { latitude: 18.5204, longitude: 73.8567 },
  Ahmedabad: { latitude: 23.0225, longitude: 72.5714 },
  Jaipur: { latitude: 26.9124, longitude: 75.7873 },
  Surat: { latitude: 21.1702, longitude: 72.8311 },
  Goa: { latitude: 15.2993, longitude: 74.124 },
  Chandigarh: { latitude: 30.7333, longitude: 76.7794 },
  Dehradun: { latitude: 30.3165, longitude: 78.0322 },
  Guwahati: { latitude: 26.1445, longitude: 91.7362 },
  Indore: { latitude: 22.7196, longitude: 75.8577 },
  Kochi: { latitude: 9.9312, longitude: 76.2673 },
};

const Clubs = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { city: cityParam } = useParams();
  const applyingCityFromUrl = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState({});
  const [filterComponent, setFilterComponent] = useState(false);
  const selectedCity = useSelector((state) => state.citySelector.selectedCity);
  const userBaseCity = useSelector((state) => state.citySelector.userBaseCity);
  const isManualCitySelection = useSelector(
    (state) => state.citySelector.isManualSelection
  );
  const selectedTrainStation = useSelector((state) => state.trainStation.selectedStation);

  const citySlug = resolveSupportedCitySlug(cityParam || selectedCity) || DEFAULT_CITY_SLUG;
  const cityLabel = getCityLabelFromSlug(cityParam || selectedCity);
  const citySeoEntry = useMemo(
    () => getClubsCityEntry(cityLabel),
    [cityLabel],
  );

  const pageSeo = useMemo(() => {
    const citySeo = resolveClubsCitySeo(cityLabel);
    if (citySeo) {
      const { h1, ...seo } = citySeo;
      return seo;
    }
    const base = resolveRouteSeo("/clubs");
    return {
      ...base,
      canonical: getCanonicalUrl(`/clubs/${citySlug}`),
    };
  }, [cityLabel, citySlug]);

  // Unknown / non-slug city → /clubs/mumbai (also /clubs/Mumbai → /clubs/mumbai)
  useEffect(() => {
    if (!cityParam) return;

    const supportedSlug = resolveSupportedCitySlug(cityParam);
    if (!supportedSlug) {
      navigate(getClubsListPath(DEFAULT_CITY_SLUG), { replace: true });
      return;
    }

    if (cityParam !== supportedSlug) {
      navigate(getClubsListPath(supportedSlug), { replace: true });
    }
  }, [cityParam, navigate]);

  // Apply /clubs/goa into Redux so listings match the URL.
  useEffect(() => {
    if (!cityParam) return;

    const label = resolveSupportedCityLabel(cityParam);
    if (!label) return;

    const alreadySelected =
      getClubsCitySlug(selectedCity) === getClubsCitySlug(label) ||
      `${selectedCity || ""}`.trim().toLowerCase() === label.toLowerCase();

    if (alreadySelected) return;

    applyingCityFromUrl.current = true;
    dispatch(currentCity(label));
    dispatch(userCurrentCity(label));
    dispatch(setSelected(true));
    dispatch(setManualSelection(true));
  }, [cityParam, dispatch, selectedCity]);

  // City change → /clubs/goa
  useEffect(() => {
    if (applyingCityFromUrl.current) {
      applyingCityFromUrl.current = false;
      return;
    }

    if (!selectedCity) return;

    const nextSlug = resolveSupportedCitySlug(selectedCity);
    const currentSlug = resolveSupportedCitySlug(cityParam);

    if (nextSlug && nextSlug !== currentSlug) {
      navigate(getClubsListPath(nextSlug), { replace: true });
    }
  }, [selectedCity, cityParam, navigate]);

  const {
    locationData,
    coords,
    locationStatus,
    permissionState,
    error: locationError,
    refreshLocation,
  } = useUserLocation();
  const requestLocationAccess = useRequestLocationAccess(refreshLocation);
  const userCoordinates = useMemo(() => {
    if (!Number.isFinite(coords?.latitude) || !Number.isFinite(coords?.longitude)) {
      return null;
    }

    return {
      latitude: Number(coords?.latitude),
      longitude: Number(coords?.longitude),
    };
  }, [coords?.latitude, coords?.longitude]);

  const locationPoint = useMemo(() => {
    if (!userCoordinates) {
      return null;
    }

    return {
      type: "Point",
      coordinates: [userCoordinates.longitude, userCoordinates.latitude],
    };
  }, [userCoordinates]);

  const hasActiveFilters = useMemo(() => {
    if (!filteredData || typeof filteredData !== "object") return false;

    const hasChecked = (list) =>
      Array.isArray(list) && list.some((item) => item?.checked);

    if (hasChecked(filteredData.locality) || hasChecked(filteredData.musicGenre)) {
      return true;
    }

    return [
      "vegNonVeg",
      "stagsAllowed",
      "kidsFriendly",
      "happyHours",
      "sheesha",
      "liveMusicDj",
    ].some((key) => Boolean(filteredData[key]));
  }, [filteredData]);

  const hasLiveCoordinates = !!userCoordinates;
  const isLocationResolved =
    locationStatus === "ready" ||
    locationStatus === "denied" ||
    locationStatus === "error" ||
    locationStatus === "unavailable";
  const preferNearbyFromGps =
    hasLiveCoordinates && !isManualCitySelection && !hasActiveFilters;
  const useLocationFilters = preferNearbyFromGps && !!locationPoint;
  const shouldWaitForLocation =
    !isManualCitySelection && !isLocationResolved;
  const showLocationPrompt =
    isLocationResolved &&
    !hasLiveCoordinates &&
    !isManualCitySelection;

  const clubsCity = isManualCitySelection
    ? selectedCity
    : preferNearbyFromGps
      ? locationData?.city || null
      : selectedCity || null;
  const clubsBaseCity = isManualCitySelection
    ? userBaseCity
    : preferNearbyFromGps
      ? locationData?.city || userBaseCity || null
      : userBaseCity || selectedCity || null;
  const canFetchClubs =
    !shouldWaitForLocation &&
    (useLocationFilters || Boolean(clubsCity) || isManualCitySelection);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = useFetchClubs(
    filteredData,
    useLocationFilters ? locationPoint : null,
    clubsCity,
    clubsBaseCity,
    useLocationFilters,
    canFetchClubs,
    useLocationFilters ? locationData : null,
    useLocationFilters ? selectedTrainStation : null
  );
  const clubs = useMemo(() => {
    const flattened =
      data?.pages
        ?.flatMap((page) => {
          const clubsArray = page?.data || [];
          return Array.isArray(clubsArray) ? clubsArray : [];
        })
        .filter(Boolean) || [];

    if (!userCoordinates) {
      return flattened;
    }

    const sortedClubs = sortByLocation(flattened, {
      coordinates: userCoordinates,
      locality: locationData?.locality || "",
      city: locationData?.city || clubsCity || "",
    });

    console.log(
      "Clubs by distance:",
      sortedClubs.slice(0, 20).map((club) => ({
        name: club?.name,
        locality: club?.locality,
        km: Number(
          getDistanceKm(
            userCoordinates,
            getItemCoordinates(club, userCoordinates)
          )?.toFixed(1)
        ),
      }))
    );

    return sortedClubs;
  }, [clubsCity, data, locationData?.city, locationData?.locality, userCoordinates]);
  const showPageLoader =
    shouldWaitForLocation || ((isLoading || isFetchingNextPage) && !clubs.length);

  const onPressApply = async (data) => {
    setFilteredData(data);
    setFilterComponent(false);
  };

  const onPressCancel = () => {
    setFilterComponent(false);
  };

  return (
    <Layout seo={pageSeo}>
      <Hero title="Clubs" />
      {/* Club Start */}
      <div
        className="hero fillter-sec section-gap wow fadeInUp"
        data-wow-delay="0.2s"
      >
        <div className="container">
          <form
            className="location-sec"
            onSubmit={(event) => {
              event.preventDefault();
              navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
            }}
          >
            <div className="row align-items-center">
              <div className="col-lg-5 mb-md-4 mb-xl-0">
                <CityDropDown
                  onPress={(city) => {
                    navigate(getClubsListPath(city));
                  }}
                />
              </div>
              <div className="col-lg-5 mb-md-4 mb-xl-0">
                <AnimatedSearchBar
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              <div className="col-lg-2">
                <div
                  className="fillter-button wow fadeInUp"
                  data-wow-delay="0.5s"
                >
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setFilterComponent(true)}
                  >
                    <i className="fa fa-cog"></i> Filter
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <PageSeoContent variant="clubs" title={citySeoEntry?.h1}/>

      {shouldWaitForLocation ? (
        <div className="container">
          <p className="text-center text-muted mb-0 section-gap-top">
            Fetching your location to show nearby clubs…
          </p>
        </div>
      ) : null}

      {preferNearbyFromGps ? (
        <div className="container">
          <p className="text-center text-muted mb-0 section-gap-top">
            Showing clubs nearest to your current location
            {locationData?.locality ? ` (${locationData.locality})` : ""}.
          </p>
        </div>
      ) : null}

      <div
        className="club-result-row section-gap-btm wow fadeInUp"
        data-wow-delay="0.2s"
      >
        <div className="container">
          {showLocationPrompt ? (
            <div className="mb-4">
              <LocationRetryPrompt
                permissionDenied={
                  permissionState === "denied" || locationStatus === "denied"
                }
                locationError={locationError}
                onRetryLocation={requestLocationAccess}
                description="Apne najdiki clubs dekhne ke liye browser se location allow karein. Neeche selected city ke clubs dikh rahe hain."
              />
            </div>
          ) : null}

          {showPageLoader ? (
            <LoadingSpinner className="spinner-container--page" />
          ) : (
            <InfiniteScroll
              dataLength={clubs?.length}
              next={() => {
                if (!isFetchingNextPage && hasNextPage) {
                  fetchNextPage();
                }
              }}
              hasMore={!!hasNextPage}
              loader={<LoadingSpinner className="spinner-container--inline" />}
              scrollThreshold={0.9}
            >
              {clubs?.length > 0 ? (
                <div className="row">
                  {clubs.map((club, index) => (
                    <Suspense
                      fallback={<CardLoader />}
                      key={club?._id ?? index.toString()}
                    >
                      <Card
                        club={club}
                        index={index}
                        city={cityLabel || selectedCity}
                      />
                    </Suspense>
                  ))}
                </div>
              ) : (
                <NoContentAvailable
                  title="No Content Available"
                  message="Club page is empty. No content available yet."
                />
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>
      {/* Search Result End */}
      {/* filter Popup */}
      <FilterComp
        isModalOpen={filterComponent}
        onPressApply={onPressApply}
        onPressCancel={onPressCancel}
        selectedFilter={filteredData}
      />
      {/* filter Popup End */}
      <Link to="#" className="back-to-top">
        <i className="fa fa-chevron-up"></i>
      </Link>
    </Layout>
  );
};

export default Clubs;
